import { RefObject, useEffect, useMemo } from "react"
import { parseValidFloat } from "../../../helpers/widget"
import { IFCViewerV4ConfigOptions } from "./config"
import { RemoteProxy } from "penpal"
import { IFrameApi } from "./shared/iframe-api"
import { OpaqueSparqlResults } from "../../../helpers/sparqlResultHelpers"
import { DEFAULT_WIDGET_CONFIG } from "../../default-widget-config"
import { ModelPresentation, ViewerConfig } from "./shared/viewer-config"
import { DEFAULT_AMBIENT_OCCLUSION_CONFIG } from "./shared/ambient-occlusion-config"

type SyncViewerConf = {
  widgetConfig: IFCViewerV4ConfigOptions
  iframeApiRef: RefObject<RemoteProxy<IFrameApi> | undefined>
  iframeLoadedAt: number
  sparqlResults: OpaqueSparqlResults | undefined
  ghostedModelUrls: ReadonlySet<string> | undefined
}

export function useSyncViewerConfig(conf: SyncViewerConf) {
  const widgetConfig = conf.widgetConfig
  const defaultConfig = DEFAULT_WIDGET_CONFIG.IFCViewerV4
  const viewerConfig = useMemo(() => {
    return ({
      display: {
        mode: widgetConfig.displayMode ?? "normal",
        transparencyOpacity: parseValidFloat(widgetConfig.transparencyOpacity),
        predefinedColors: widgetConfig.predefinedColors,
        assignRandomColorsToTags: widgetConfig.assignRandomColorsToTags ?? defaultConfig.assignRandomColorsToTags,
        legendSorting: widgetConfig.legendSorting ?? defaultConfig.legendSorting,
        modelPresentation: toModelPresentation(conf.ghostedModelUrls ?? new Set())
      },
      selection: {
        zoomToSelection: Boolean(widgetConfig.zoomToSelection)
      },
      controls: {
        infinityDolly: widgetConfig.infinityDolly ?? defaultConfig.infiniteDolly,
        dollyToCursor: widgetConfig.dollyToCursor ?? defaultConfig.dollyToCursor,
        verticalDragToForward: widgetConfig.verticalDragToForward ?? defaultConfig.verticalDragToForward,
        minDollyDistance: parseValidFloat(widgetConfig.minDollyDistance) ?? defaultConfig.minDollyDistance,
        maxDollyDistance: parseValidFloat(widgetConfig.maxDollyDistance) ?? defaultConfig.maxDollyDistance
      },
      rendering: {
        doubleSided: widgetConfig.materialsDoubleSided ?? undefined,
        alphaHash: widgetConfig.materialsAlphaHash ?? undefined,
        postproduction: {
          enabled: widgetConfig.postproductionEnabled ?? defaultConfig.postproductionEnabled,
          style: widgetConfig.postproductionStyle ?? defaultConfig.postproductionStyle,
          smaa: widgetConfig.postproductionSmaa ?? defaultConfig.postproductionSmaa,
          glossEnabled: widgetConfig.postproductionGlossEnabled ?? defaultConfig.postproductionGlossEnabled,
          ambientOcclusion: widgetConfig.ambientOcclusionConfig ?? DEFAULT_AMBIENT_OCCLUSION_CONFIG,
          edges: {
            width: parseValidFloat(widgetConfig.edgesWidth) ?? defaultConfig.edgesWidth,
            color: widgetConfig.edgesColor || defaultConfig.edgesColor,
            mode: widgetConfig.edgesMode ?? defaultConfig.edgesMode,
          }
        }
      },
      background: widgetConfig.backgroundSkybox
        ? { type: "skybox", skybox: widgetConfig.backgroundSkybox }
        : { type: "css", value: widgetConfig.backgroundCss || defaultConfig.backgroundCss },
      showGrid: widgetConfig.showGrid ?? defaultConfig.showGrid
    } satisfies ViewerConfig)
  }, [ widgetConfig, defaultConfig, conf.ghostedModelUrls ])

  useEffect(function syncViewerConfig() {
    if (!conf.iframeApiRef.current) return
    conf.iframeApiRef.current.setViewerConfig(viewerConfig).catch(console.error)
  }, [ conf.iframeApiRef, conf.iframeLoadedAt, viewerConfig
  ])

  useEffect(function syncSparqlResults() {
    if (!conf.sparqlResults) return
    if (!conf.iframeApiRef.current) return
     conf.iframeApiRef.current.setSparqlResults(conf.sparqlResults)
      .catch(console.error)
  }, [ conf.iframeApiRef, conf.iframeLoadedAt, conf.sparqlResults ])
}

function toModelPresentation(ghostedModelUrls: ReadonlySet<string>) {
  const presentation: Record<string, ModelPresentation> = {}
  for (const url of ghostedModelUrls) presentation[url] = "ghost"
  return presentation
}
